import * as babylon from "@babylonjs/core";

import {
  ancestorChain,
  descendantIds,
} from "@/shared/state/visualization/schema/v1/three-d";

import { BabylonState, Entity } from "../../state";
import { localPositionAt } from "../render/setPositionsToTime";
import { localRotationAt } from "../render/setRotationsToTime";

import { mergeLogTimelines } from "./reloadVectorFields";

/**
 * Rebuilds the absolute trajectory of any entity whose global cache is stale.
 *
 * A child's `Position` cache holds offsets in its parent's frame, and the parent
 * moves — so the child's real path through the world is not any rigid transform
 * of its local one. It has to be reconstructed sample by sample: at each time
 * where *any* link in the chain has a sample, sum every link's position there.
 *
 * Without this, a trace baked in the parent's frame would disagree with both the
 * entity's own mesh and the camera, which work in absolute space.
 *
 * Runs on the reload poll rather than per frame — the result is static geometry.
 * Must run after `reloadStalePositions` (it consumes local caches) and before
 * `reloadStalePaths` / `reloadStaleVectorFieldMeshes` (they consume this one).
 */
export function reloadStaleGlobalPositions(state: BabylonState) {
  for (const id of state.sceneConfig.entityOrder ?? []) {
    const entity = state.entities[id];
    const global = entity?.globalPosition;
    if (!global || global.cache.load.state !== "stale") {
      continue;
    }

    // Root-first. `ancestorChain` returns self-first, which was fine while this
    // was a sum; composing rotation is not commutative, so the order the frame
    // pass walks — parents before children — is now the only correct one.
    const chain = ancestorChain(specParents(state), id)
      .map((ancestorId) => state.entities[ancestorId])
      .reverse();
    if (!chain.every(isReady)) {
      // A local cache in the chain is still loading. Leave this entity stale and
      // pick it up on a later tick rather than baking a partial trajectory.
      continue;
    }

    // Wherever *any* link in the chain has a sample, in position or orientation:
    // a parent turning between two of a child's samples moves the child, and a
    // grid that ignored it would cut the corner.
    const grid = mergeLogTimelines(
      chain.flatMap((ancestor) => [
        ancestor.position?.cache.logtimes ?? [],
        ancestor.rotation?.cache.logtimes ?? [],
      ]),
    );

    const allPoints: babylon.Vector3[] = [];
    for (const time of grid) {
      const point = babylon.Vector3.Zero();
      const rotation = babylon.Quaternion.Identity();
      for (const ancestor of chain) {
        // Mirrors `resolveGlobalPositions` exactly: place this link's offset by
        // everything accumulated above it, then fold in its own orientation.
        if (ancestor.position) {
          const local = localPositionAt(ancestor.position.cache, time);
          if (local) {
            point.addInPlace(local.applyRotationQuaternion(rotation));
          }
        }
        if (ancestor.rotation) {
          const local = localRotationAt(ancestor.rotation.cache, time);
          if (local) {
            rotation.multiplyInPlace(local);
          }
        }
      }
      allPoints.push(point);
    }

    global.cache.logtimes = grid;
    global.cache.allPoints = allPoints;
    global.mean = meanOf(allPoints);
    global.cache.load.state = "loaded";

    // Path and vector-field geometry is baked from this cache and anchored at
    // this mean, so both are now out of date.
    if (entity.path) {
      entity.path.load.state = "stale";
    }
    if (entity.vectorField) {
      entity.vectorField.meshStale = true;
    }
  }
}

/**
 * Marks an entity's global trajectory stale, and every descendant's with it.
 *
 * A descendant's absolute path is summed through this entity, so a change here
 * invalidates everything below — the one dependency in this package that runs
 * *down* the hierarchy rather than within a single entity.
 *
 * Called when an entity's local position data is reloading and when its
 * `Position` component is added or removed. A `GlobalPosition.spec` change is
 * handled by `updateGlobalPosition` itself, which owns that field.
 */
export function invalidateGlobalPositions(
  state: BabylonState,
  entityId: string,
) {
  const parents = specParents(state);
  for (const id of [entityId, ...descendantIds(parents, entityId)]) {
    const global = state.entities[id]?.globalPosition;
    if (global) {
      global.cache.load.state = "stale";
    }
  }
}

/**
 * Whether this link can contribute to a composed trajectory yet.
 *
 * An entity with no `Position` is ready by definition — it contributes zero.
 * That matters: a structural hierarchy node has no data to load, and treating it
 * as "not ready" would stall every descendant forever.
 */
function isReady(entity: Entity | undefined): boolean {
  if (!entity) {
    return false;
  }
  // Both halves of the transform have to have landed. A rotation still loading
  // would otherwise be read as identity and bake a trajectory that ignores it.
  return (
    (entity.position === undefined ||
      entity.position.cache.load.state === "loaded") &&
    (entity.rotation === undefined ||
      entity.rotation.cache.load.state === "loaded")
  );
}

/**
 * The hierarchy as `ancestorChain` wants it, read back off the ECS.
 *
 * `GlobalPosition.spec` is the authoritative parent on this side of the divide —
 * the panel state that produced it is not retained — so the shape is rebuilt from
 * the components. Cheap: this runs on the 50ms poll, only for stale entities.
 */
function specParents(state: BabylonState): Record<string, { parent?: string }> {
  const parents: Record<string, { parent?: string }> = {};
  for (const [id, entity] of Object.entries(state.entities)) {
    parents[id] = { parent: entity.globalPosition?.spec };
  }
  return parents;
}

/** Componentwise mean, or the origin for an empty trajectory. */
function meanOf(points: babylon.Vector3[]): babylon.Vector3 {
  if (points.length === 0) {
    return babylon.Vector3.Zero();
  }
  const total = babylon.Vector3.Zero();
  for (const point of points) {
    total.addInPlace(point);
  }
  return total.scale(1 / points.length);
}
